import { useEffect, useState } from "react";
import {
  getAddressAlignment,
  getAddressDisplayMode,
  getAddressPinBorderMode,
  getAddressPlacePosition,
  getAddressPlaceReference,
  getAddressPlaceVisible,
  getAddressTextPosition,
  getFlexJustifyValue,
  getTextAlignValue,
} from "../utils/layoutUtils";

function isMobileDevice() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;

  const userAgent = navigator.userAgent || navigator.vendor || "";
  const mobileUserAgent = /android|iphone|ipad|ipod|windows phone|mobile/i.test(userAgent);
  const coarsePointer = window.matchMedia?.("(pointer: coarse)")?.matches;
  const narrowViewport = window.matchMedia?.("(max-width: 768px)")?.matches;
  return mobileUserAgent || (coarsePointer && narrowViewport);
}

function isVertical(position) {
  return position === "top" || position === "bottom";
}

function buildWazeUrl(endereco) {
  if (!endereco) return null;
  return `https://waze.com/ul?q=${encodeURIComponent(endereco)}&navigate=yes`;
}

function WazeIcon({ size = 26 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      aria-hidden="true"
      focusable="false"
      style={{ display: "block" }}
    >
      <path
        d="M12 36.5c-4.1-.4-6.5-2.6-7.1-6.6 4.7-.2 7.1-3.2 7.1-9.1C12 11.9 20.2 5 32.2 5 45.5 5 56 14 56 27.8 56 40.6 46.3 50 32.7 50c-5.2 0-9.8-1.2-13.5-3.5L9.3 51.8c-.7.4-1.6-.3-1.3-1.1l4-14.2Z"
        fill="#ffffff"
        stroke="#111111"
        strokeWidth="5"
        strokeLinejoin="round"
      />
      <circle cx="24" cy="27" r="3.8" fill="#111111" />
      <circle cx="40" cy="27" r="3.8" fill="#111111" />
      <path
        d="M23.5 35.5c4 5.7 12.7 5.7 17 0"
        fill="none"
        stroke="#111111"
        strokeWidth="5"
        strokeLinecap="round"
      />
      <circle cx="22" cy="52" r="7" fill="#111111" />
      <circle cx="43" cy="52" r="7" fill="#111111" />
    </svg>
  );
}

function makePositionedGroup(primary, secondary, position, gap, groupStyle) {
  if (!primary) return secondary;
  if (!secondary) return primary;

  const secondaryFirst = position === "top" || position === "left";

  return (
    <span
      style={{
        display: "inline-flex",
        flexDirection: isVertical(position) ? "column" : "row",
        alignItems: "center",
        justifyContent: "center",
        gap,
        minWidth: 0,
        ...groupStyle,
      }}
    >
      {secondaryFirst ? secondary : primary}
      {secondaryFirst ? primary : secondary}
    </span>
  );
}

export default function EventAddressLink({
  layoutConfig,
  mapsUrl,
  endereco,
  nomeEspaco,
  color,
  fontSize = 14,
  fontWeight = 500,
  iconSize = 18,
  gap = 12,
  defaultMode = "pinText",
  defaultAlign = "center",
  defaultPinBg = "#ffa726",
  defaultPinBorderMode = "none",
  defaultPinBorderColor = null,
  pinSize = 46,
  pinRadius = 14,
  style,
  textStyle,
  placeStyle,
  iconStyle,
}) {
  const [showWazeLink, setShowWazeLink] = useState(false);
  const mode = getAddressDisplayMode(layoutConfig, defaultMode);
  const alignment = getAddressAlignment(layoutConfig, defaultAlign);
  const textPosition = getAddressTextPosition(layoutConfig);
  const placePosition = getAddressPlacePosition(layoutConfig);
  const placeReference = getAddressPlaceReference(layoutConfig);
  const showPin = mode === "pin" || mode === "pinText";
  const showText = mode === "text" || mode === "pinText";
  const showPlace = getAddressPlaceVisible(layoutConfig) && !!nomeEspaco;
  const justifyPair = alignment === "justify" && showPin && showText && !isVertical(textPosition);
  const textAlign = getTextAlignValue(alignment);
  const justifyContent = getFlexJustifyValue(alignment, true);
  const pinBg = layoutConfig?.addrPinBg ?? defaultPinBg;
  const selectedPinBorderColor = layoutConfig?.addrPinBorderColor;
  const rawPinBorderMode = getAddressPinBorderMode(layoutConfig, defaultPinBorderMode);
  const pinBorderMode = rawPinBorderMode === "default" && selectedPinBorderColor ? "custom" : rawPinBorderMode;
  const pinBorder = pinBorderMode === "custom"
    ? (selectedPinBorderColor
      ? `2px solid ${selectedPinBorderColor}`
      : (defaultPinBorderColor ? `2px solid ${defaultPinBorderColor}` : "none"))
    : (pinBorderMode === "none" ? "none" : (defaultPinBorderColor ? `2px solid ${defaultPinBorderColor}` : "none"));
  const wazeUrl = buildWazeUrl(endereco);
  const previewDevice = layoutConfig?.__previewDevice;
  const showWazeForContext = previewDevice ? previewDevice === "mobile" : showWazeLink;
  const showWazeButton = layoutConfig?.addrShowWaze !== false && showPin && showWazeForContext && !!wazeUrl;
  const wazeButtonSize = pinSize;

  useEffect(() => {
    setShowWazeLink(isMobileDevice());
  }, []);

  const pinVisualNode = showPin ? (
    <span
      aria-hidden="true"
      style={{
        width: pinSize,
        height: pinSize,
        borderRadius: pinRadius,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: pinBg,
        border: pinBorder,
        boxSizing: "border-box",
        boxShadow: "0 5px 15px rgba(0, 0, 0, 0.18)",
        fontSize: iconSize,
        lineHeight: 1,
        flexShrink: 0,
        ...iconStyle,
      }}
    >
      📍
    </span>
  ) : null;

  const googlePinNode = showWazeButton ? (
    <a
      href={mapsUrl}
      target="_blank"
      rel="noreferrer"
      aria-label="Abrir no Google Maps"
      title="Abrir no Google Maps"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        textDecoration: "none",
      }}
    >
      {pinVisualNode}
    </a>
  ) : pinVisualNode;

  const wazeNode = showWazeButton ? (
    <a
      href={wazeUrl}
      target="_blank"
      rel="noreferrer"
      aria-label="Abrir no Waze"
      title="Abrir no Waze"
      style={{
        width: wazeButtonSize,
        height: wazeButtonSize,
        borderRadius: "50%",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        background: "#33ccff",
        color: "#111827",
        border: "2px solid rgba(255,255,255,0.78)",
        boxSizing: "border-box",
        boxShadow: "0 5px 15px rgba(0,0,0,0.2)",
        textDecoration: "none",
        lineHeight: 1,
      }}
    >
      <WazeIcon size={Math.max(24, Math.round(wazeButtonSize * 0.68))} />
    </a>
  ) : null;

  let pinNode = showPin ? (
    showWazeButton ? (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: Math.max(8, Math.round(gap / 2)),
          flexShrink: 0,
        }}
      >
        {googlePinNode}
        {wazeNode}
      </span>
    ) : googlePinNode
  ) : null;

  const textNodeStyle = {
    fontSize,
    fontWeight,
    lineHeight: 1.4,
    textAlign: justifyPair ? (textPosition === "left" ? "left" : "right") : textAlign,
    flex: justifyPair ? 1 : undefined,
    minWidth: 0,
    ...textStyle,
  };

  let textNode = showText ? (
    showWazeButton ? (
      <a
        href={mapsUrl}
        target="_blank"
        rel="noreferrer"
        title="Ver no mapa"
        style={{
          ...textNodeStyle,
          color: "inherit",
          display: "inline-block",
          textDecoration: "none",
        }}
      >
        {endereco}
      </a>
    ) : (
      <span style={textNodeStyle}>
        {endereco}
      </span>
    )
  ) : null;

  const placeNode = showPlace ? (
    <span
      style={{
        fontSize,
        fontWeight: 800,
        lineHeight: 1.25,
        textAlign,
        minWidth: 0,
        ...placeStyle,
      }}
    >
      {nomeEspaco}
    </span>
  ) : null;

  if (placeNode && placeReference === "pin" && pinNode) {
    pinNode = makePositionedGroup(pinNode, placeNode, placePosition, Math.max(6, Math.round(gap / 2)));
  } else if (placeNode && placeReference === "text" && textNode) {
    textNode = makePositionedGroup(textNode, placeNode, placePosition, Math.max(6, Math.round(gap / 2)));
  }

  const addressPair = makePositionedGroup(
    pinNode,
    textNode,
    textPosition,
    gap,
    justifyPair ? { width: "100%", justifyContent: "space-between" } : undefined
  );
  const content = placeNode && (
    (placeReference === "block") ||
    (placeReference === "pin" && !pinNode) ||
    (placeReference === "text" && !textNode)
  )
    ? makePositionedGroup(addressPair, placeNode, placePosition, gap)
    : addressPair;

  if (!content) return null;

  const linkStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent,
    width: "100%",
    textDecoration: "none",
    color,
    textAlign,
    minWidth: 0,
  };

  if (showWazeButton) {
    return (
      <span
        style={{
          ...linkStyle,
          color,
          cursor: "default",
          ...style,
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: alignment === "justify" ? "100%" : undefined,
            minWidth: 0,
            maxWidth: "100%",
          }}
        >
          {content}
        </span>
      </span>
    );
  }

  const contentNode = (
    <a
      href={mapsUrl}
      target="_blank"
      rel="noreferrer"
      title="Ver no mapa"
      style={{ ...linkStyle, ...style }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: alignment === "justify" ? "100%" : undefined,
          minWidth: 0,
          maxWidth: "100%",
        }}
      >
        {content}
      </span>
    </a>
  );

  return contentNode;
}
